/**
 * Wrapper that renders all User Management modals based on hook state.
 *
 * Extracted from UserManagementPage.tsx (Sub-PR 6.3).
 */

import { CreateUserModal } from './CreateUserModal'
import { EditUserModal } from './EditUserModal'
import { DeleteUserModal } from './DeleteUserModal'
import { SeatAssignmentModal } from './SeatAssignmentModal'
import { ReactivateModal } from './ReactivateModal'
import { ResetPasswordModal } from './ResetPasswordModal'
import { IssuedPasswordModal } from './IssuedPasswordModal'
import type { useUserManagement } from './use-user-management'

type Mgmt = ReturnType<typeof useUserManagement>

export function UserManagementModals({ mgmt }: { mgmt: Mgmt }): React.ReactElement {
  return (
    <>
      <FormModals mgmt={mgmt} />
      <ConfirmationModals mgmt={mgmt} />
      {mgmt.issuedPassword !== null && (
        <IssuedPasswordModal
          password={mgmt.issuedPassword}
          onClose={() => {
            mgmt.setIssuedPassword(null)
          }}
        />
      )}
    </>
  )
}

function ReactivateModalSection({ mgmt }: { mgmt: Mgmt }): React.ReactElement {
  if (!mgmt.showReactivateModal || !mgmt.userToReactivate) return <></>
  return (
    <ReactivateModal
      user={mgmt.userToReactivate}
      reactivateLicenses={mgmt.reactivateLicenses}
      selectedLicense={mgmt.selectedLicense}
      setSelectedLicense={mgmt.setSelectedLicense}
      formError={mgmt.formError}
      formLoading={mgmt.formLoading}
      onClose={() => {
        mgmt.setShowReactivateModal(false)
        mgmt.setUserToReactivate(null)
        mgmt.setReactivateLicenses([])
        mgmt.setSelectedLicense('')
        mgmt.setFormError(null)
      }}
      onConfirm={() => {
        void mgmt.handleConfirmReactivate()
      }}
    />
  )
}

function FormModals({ mgmt }: { mgmt: Mgmt }): React.ReactElement {
  return (
    <>
      {mgmt.showCreateModal && (
        <CreateUserModal
          formData={mgmt.formData}
          setFormData={mgmt.setFormData}
          formError={mgmt.formError}
          formLoading={mgmt.formLoading}
          isPlatformAdmin={mgmt.isPlatformAdmin}
          organisations={mgmt.organisations}
          onClose={() => {
            mgmt.setShowCreateModal(false)
          }}
          onSubmit={(e) => {
            void mgmt.handleCreateUser(e)
          }}
        />
      )}
      {mgmt.showEditModal && mgmt.selectedUser && (
        <EditUserModal
          formData={mgmt.formData}
          setFormData={mgmt.setFormData}
          formError={mgmt.formError}
          formLoading={mgmt.formLoading}
          isPlatformAdmin={mgmt.isPlatformAdmin}
          organisations={mgmt.organisations}
          onClose={() => {
            mgmt.setShowEditModal(false)
          }}
          onSubmit={(e) => {
            void mgmt.handleUpdateUser(e)
          }}
        />
      )}
      {mgmt.showSeatModal && mgmt.selectedUser && (
        <SeatAssignmentModal
          user={mgmt.selectedUser}
          licenses={mgmt.licenses}
          selectedLicense={mgmt.selectedLicense}
          setSelectedLicense={mgmt.setSelectedLicense}
          formError={mgmt.formError}
          formLoading={mgmt.formLoading}
          onClose={() => {
            mgmt.setShowSeatModal(false)
            mgmt.setSelectedUser(null)
            mgmt.setSelectedLicense('')
            mgmt.setFormError(null)
          }}
          onAssign={() => {
            void mgmt.handleAssignSeat()
          }}
        />
      )}
      <ReactivateModalSection mgmt={mgmt} />
    </>
  )
}

function ConfirmationModals({ mgmt }: { mgmt: Mgmt }): React.ReactElement {
  return (
    <>
      {mgmt.showDeleteModal && mgmt.selectedUser && (
        <DeleteUserModal
          user={mgmt.selectedUser}
          formError={mgmt.formError}
          formLoading={mgmt.formLoading}
          onClose={() => {
            mgmt.setShowDeleteModal(false)
          }}
          onConfirm={() => {
            void mgmt.handleDeleteUser()
          }}
        />
      )}
      {mgmt.showResetPasswordModal && mgmt.selectedUser && (
        <ResetPasswordModal
          user={mgmt.selectedUser}
          formError={mgmt.formError}
          formLoading={mgmt.formLoading}
          onClose={() => {
            mgmt.setShowResetPasswordModal(false)
            mgmt.setSelectedUser(null)
            mgmt.setFormError(null)
          }}
          onConfirm={() => {
            void mgmt.handleResetPassword()
          }}
        />
      )}
    </>
  )
}
